<template>
  <BaseModal
    card-width="488px"
    no-padding
    no-toolbar-padding
    no-exit
    @close="$emit('close')"
  >
    <template #toolbar>
      <div>
        <div
          class="w-full flex items-start gap-3"
        >
          <DuplicateFolderIcon class="text-icon-muted" />
          <div>
            <BaseText
              type="label"
              size="sm"
              class="text-text-muted"
            >
              Duplicate Folder
            </BaseText>
            <BaseText
              size="sm"
              class="text-text-normal"
            >
              Duplicating this folder will also duplicate all reports.
            </BaseText>
          </div>
        </div>
      </div>
      <button
        class="absolute top-3 right-3 p-1.5"
        @click="$emit('close')"
      >
        <TagRemoveIcon stroke="#5E6678" />
      </button>
    </template>
    <template #default>
      <BaseHeightTransition>
        <!-- MODAL SECTION - Duplicate report details -->
        <div
          class="w-full flex flex-col items-stretch"
        >
          <!-- Modal body -->
          <div class="w-full h-full p-5 border-t border-b mt-4 flex flex-col gap-4">
            <!-- Ad Account -->
            <div class="w-full space-y-1">
              <div v-if="loading.lensAccounts">
                <div class="w-full skeleton h-5 rounded-lg" />
                <div class="w-full skeleton h-8 rounded-lg" />
              </div>
              <div v-else>
                <div class="flex items-center gap-1 mt-3">
                  <BaseText
                    type="label"
                    size="sm"
                    class="text-text-muted"
                  >
                    Destination Lens Account(s)
                  </BaseText>
                </div>
                <BaseMultiSelect
                  class="mt-1 z-50"
                  :items="otherLensAccounts"
                  :selections.sync="dupeForm.selectedLens"
                  :max-dropdown-height="180"
                  empty-label="Select lens"
                />
              </div>
              <div
                v-show="showError.lens"
                class="flex items-center gap-1"
              >
                <AltAlertIcon class="text-secondary-red-100" />
                <BaseText
                  type="body"
                  size="xs"
                  class="text-secondary-red-100"
                >
                  Required
                </BaseText>
              </div>
            </div>
            <!-- Name -->
            <div class="space-y-1">
              <BaseText
                type="label"
                size="sm"
                class="text-text-muted"
              >
                Folder Name
              </BaseText>
              <input
                v-model="dupeForm.name"
                :class="{ 'invalid': showError.name }"
                placeholder="Name"
                class="form-input fade-in min-w-0 w-full px-2 py-1.5"
              >
              <!-- invalid indicator -->
              <div
                v-show="showError.name"
                class="flex items-center gap-1"
              >
                <AltAlertIcon class="text-secondary-red-100" />
                <BaseText
                  type="body"
                  size="xs"
                  class="text-secondary-red-100"
                >
                  Required
                </BaseText>
              </div>
            </div>
          </div>
          <!-- Modal footer -->
          <div class="w-full flex justify-between items-center p-5">
            <BaseButton
              class="px-2 py-1.5 rounded-md transition-colors hover:bg-neutral-25"
              @click="$emit('close')"
            >
              Cancel
            </BaseButton>
            <button
              class="black-btn-primary submit-btn py-1.5 px-3 rounded-md flex items-center justify-center text-white"
              style="width: 88px"
              :disabled="loading.submit"
              @click="duplicateFolder"
            >
              <BaseLoadingSpinnerCircle v-if="loading.submit" />
              <BaseText
                v-else
                type="label"
                size="sm"
              >
                Duplicate
              </BaseText>
            </button>
          </div>
        </div>
      </BaseHeightTransition>
    </template>
  </BaseModal>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import LensAPI from '@/api/lens'
// Icons
import DuplicateFolderIcon from '../globals/Icons/DuplicateFolderIcon.vue'
import AltAlertIcon from '../globals/Icons/AltAlertIcon.vue'
import TagRemoveIcon from '../globals/Icons/TagRemoveIcon.vue'
export default {
  name: 'DuplicateFolderModal',
  components: {
    DuplicateFolderIcon,
    AltAlertIcon,
    TagRemoveIcon
  },
  props: {
    folder: {
      type: Object,
      default: () => {}
    }
  },
  data () {
    return {
      // Modal state
      createdNewReport: false,

      // options
      otherLensAccounts: [],

      // Form
      submitAttempted: false,
      dupeForm: {
        selectedLens: [],
        name: ''
      },
      loading: {
        lensAccounts: true,
        submit: false
      }
    }
  },
  computed: {
    ...mapGetters('LensModule', ['getLensFolders', 'getSidebarLenses']),
    showError () {
      return {
        lens: (!this.dupeForm.selectedLens || this.dupeForm.selectedLens.length === 0) && this.submitAttempted,
        name: !this.dupeForm.name && this.submitAttempted
      }
    },
    isFormValid () {
      return this.dupeForm.name && this.dupeForm.selectedLens && this.dupeForm.selectedLens.length > 0
    }
  },
  async mounted () {
    this.loading.lensAccounts = true

    // Init form with copied data
    if (this.folder.name) { this.dupeForm.name = `${this.folder.name} (Copy)` }
    console.log('FOLDER IN QUER', this.folder, this.getSidebarLenses)

    try {
      const lensAccounts = this.getSidebarLenses?.length > 0 ? this.getSidebarLenses : (await LensAPI.Lens.getAllSidebar())?.data
      this.otherLensAccounts = lensAccounts?.map((lens) => { return { id: lens.id, name: lens.name } })
    } catch (error) {
      // TODO @Sam: update later
      this.$showAlert({
        type: 'error',
        message: 'Could not fetch lens account, please try refreshing'
      })
      console.error('An error occurred while fetching lens accounts', error)
    } finally {
      this.loading.lensAccounts = false
    }
  },
  methods: {
    ...mapActions('LensModule', ['fetchSidebarLenses']),
    async duplicateFolder () {
      this.submitAttempted = true
      if (!this.isFormValid) return
      this.loading.submit = true
      try {
        const duplicateRes = await LensAPI.LensFolder.duplicate(this.folder.id, { folderName: this.dupeForm.name, lensDestinations: this.dupeForm.selectedLens })
        console.log('DUPLICATE RES', duplicateRes)
        // Refetch
        console.log('%c Report successfully duplicated', 'color: orange')
        await this.fetchSidebarLenses()
      } catch (error) {
        console.error(error)
        this.$showAlert({
          type: 'error',
          message: 'Error duplicating report'
        })
      } finally {
        this.loading.submit = false
        // this.createdNewReport = true
        this.$showAlert({ type: 'success', message: 'Report successfully duplicated' })
        this.$emit('close')
      }
    },
    navToReport () {
      const { lensId, reportId } = this.newReportNav
      // const reportUrl = `${process.env.VUE_APP_BASE_URL}/lens/${lensId}/${reportId}`
      this.$emit('close')
      this.$router.push({ name: 'LensReport', params: { lensId, reportId } })
    }
  }
}
</script>

<style scoped>
.submit-btn:disabled{
  box-shadow:none;
  background-color:#F6F8FA;
  color: #A4acb9;
  cursor: not-allowed
}
.form-input {
  color: #06070F;
  border-radius: 6px;
  box-shadow: 0px 1px 2px 0px rgba(0, 56, 108, 0.08), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
  transition: box-shadow 150ms ease-in-out;
}
.form-input:focus {
  outline: none;
  box-shadow: 0px 1px 2px 0px rgba(0, 56, 108, 0.1), 0px 0px 0px 1px rgba(0, 56, 108, 0.1);
}
.form-input::placeholder {
  color: #5E6678;
  transition: color 150ms ease-in-out;
  opacity: 0.9;
}
.form-input:hover::placeholder {
  color: #303546;
}
.form-input:focus::placeholder {
  color: #C1C7D0;
}

.invalid-dropdown{
  box-shadow: none;
  border: 1px solid #FF002F;
}
.form-input.invalid {
  box-shadow: none;
  border: 1px solid #FF002F;
}

/* Section transitions */
.expand-enter-active,
.expand-leave-active {
  transition-property: opacity, height;
  transition-duration: 200ms;
}

.expand-enter,
.expand-leave-to {
  opacity: 0;
}

.fade-in {
  animation: fadeIn 150ms ease-in-out forwards;
}
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style>
